import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// Types
// ============================================================================

export interface CustomerTank {
  id: string;
  name: string;
  capacityTotal: number;
  capacityUsed: number;
}

export interface Customer {
  id: string;
  name: string;
  vatId: string;
  customerNumber: string;
  additionalDetails: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  country: string;
  primaryEmail: string;
  customerTanks: CustomerTank[];
  notes: string;
  externalCustomerId: string;
  setReferenceFromTruckScale: boolean;
  isActive: boolean;
  isEntrySendingEnabled: boolean;
  isSurfaceRadarNotificationEnabled: boolean;
  notificationEmails: string[];
  terminalIds: string[];
}

export interface CreateCustomerCmd {
  name: string;
  customerNumber?: string;
  vatId?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  primaryEmail?: string;
  externalCustomerId?: string;
  notes?: string;
  setReferenceFromTruckScale?: boolean;
  isSurfaceRadarNotificationEnabled?: boolean;
  isEntrySendingEnabled?: boolean;
  notificationEmails?: string[];
}

export interface EditCustomerCmd {
  id?: string;
  // Core fields
  name: string;
  customerNumber?: string;
  vatId?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  primaryEmail?: string;
  isActive?: boolean;
  // Configuration fields
  externalCustomerId?: string;
  setReferenceFromTruckScale?: boolean;
  isSurfaceRadarNotificationEnabled?: boolean;
  isEntrySendingEnabled?: boolean;
  notificationEmails?: string[];
  notes?: string;
}

export interface CustomerSyncStatistics {
  timestamp: string;
  created: number;
  updated: number;
  activated: number;
  deactivated: number;
}

export interface CustomerSyncResult {
  statistics: CustomerSyncStatistics;
  customers: Customer[];
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'customers';

const fetchCustomers = async (): Promise<Customer[]> => {
  return apiRequest<void, Customer[]>(HttpMethod.GET, API_ENDPOINT);
};

const fetchCustomer = async (id: string): Promise<Customer> => {
  return apiRequest<void, Customer>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const createCustomer = async (customer: CreateCustomerCmd): Promise<Customer> => {
  return apiRequest<CreateCustomerCmd, Customer>(HttpMethod.POST, API_ENDPOINT, customer);
};

const updateCustomer = async (customer: EditCustomerCmd): Promise<Customer> => {
  return apiRequest<EditCustomerCmd, Customer>(
    HttpMethod.PUT,
    `${API_ENDPOINT}/${customer.id}`,
    customer
  );
};

const deleteCustomer = async (id: string): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${id}`);
};

const syncFromProcountor = async (): Promise<CustomerSyncResult> => {
  return apiRequest<void, CustomerSyncResult>(HttpMethod.POST, `${API_ENDPOINT}/sync`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all customers
 */
export const useCustomersQuery = () => {
  return useQuery({
    queryKey: queryKeys.customers.all,
    queryFn: fetchCustomers,
  });
};

/**
 * Fetch a single customer by ID
 */
export const useCustomerQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: queryKeys.customers.detail(id!),
    queryFn: () => fetchCustomer(id!),
    enabled: !!id,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new customer
 */
export const useCreateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update a customer
 */
export const useUpdateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a customer
 */
export const useDeleteCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toastService.success({ title: 'delete_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'delete_failed');
    },
  });
};

/**
 * Sync customers from Procountor
 */
export const useSyncFromProcountorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncFromProcountor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toastService.success({ title: 'customer.update_from_procountor_success' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
